import { z } from "zod";

import { jurisdictionProfileSchema } from "../services/cableContract";
import {
  backupDeviceKindSchema,
  connectionModeSchema,
  coordinationDeclarationSchema,
  earthingSystemSchema,
  locationKindSchema,
  lpsPresenceSchema,
  spdTypeSchema,
  upBasisSchema,
} from "../services/surgeProtectionContract";
import {
  BACKUP_DEVICE_KIND_LABELS,
  CONNECTION_MODE_LABELS,
  COORDINATION_LABELS,
  EARTHING_SYSTEM_LABELS,
  LOCATION_LABELS,
  LPS_LABELS,
  SPD_TYPE_LABELS,
  SURGE_PROTECTION_LABELS,
  UP_BASIS_LABELS,
} from "./surgeProtectionLabels";

// Draft model of the surge protection form (EOS-10, A35): what the user is typing, kept apart
// from the components so the payload rules are tested without a browser.
//
// Every value is held as the string the user typed. The prospective current comes from a stored
// EOS-04 fault run of the active revision (LINKED, the default), is typed (DECLARED) or is left
// not established (NONE). The leads are declared as a voltage drop (VOLTAGE), as a length with
// its inductive drop (LENGTH) or not at all (NONE). A blank optional item (a source, UT, Imax,
// the rating, the protection distance, a requirement, an item's distance) is left OUT of the
// payload: blank means NOT ESTABLISHED, and the engine must see it as absent, never as "". A
// required figure is sent as "" so the schema names it by its on-screen label; a choice the user
// has not made ("" = Choose...) is left out so the schema reports it as required.
//
// Items are separate entries with stable ids, so adding or removing one never disturbs the
// others; the ids never reach the backend.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const SURGE_PROTECTION_DRAFT_MODULE = "surge-protection";

/** Where the prospective current comes from, on screen. */
export const CURRENT_SOURCES = ["LINKED", "DECLARED", "NONE"] as const;
export type CurrentSourceChoice = (typeof CURRENT_SOURCES)[number];

/** How the leads are declared, on screen. */
export const LEAD_ROUTES = ["LENGTH", "VOLTAGE", "NONE"] as const;
export type LeadRouteChoice = (typeof LEAD_ROUTES)[number];

export type ProtectedItemDraft = {
  id: string;
  code: string;
  name: string;
  uwKv: string;
  uwSource: string;
  distanceM: string;
  notes: string;
};

export type SurgeProtectionDraft = {
  code: string;
  name: string;
  spdCode: string;
  locationKind: string;
  systemVoltageV: string;
  systemVoltageSource: string;
  earthingSystem: string;
  connectionMode: string;
  connectionModeSource: string;
  sldBusCode: string;
  currentSource: CurrentSourceChoice;
  faultRunId: string;
  prospectiveCurrentKa: string;
  prospectiveCurrentSource: string;
  lpsPresence: string;
  spdType: string;
  upKv: string;
  upSource: string;
  upBasis: string;
  ucV: string;
  ucSource: string;
  utV: string;
  utSource: string;
  iimpKa: string;
  iimpSource: string;
  inKa: string;
  inSource: string;
  imaxKa: string;
  imaxSource: string;
  iimpPerModeDeclared: boolean;
  isccrKa: string;
  isccrSource: string;
  backupKind: string;
  backupRatingA: string;
  backupSource: string;
  backupSldDeviceCode: string;
  protectionDistanceM: string;
  protectionDistanceSource: string;
  coordination: string;
  coordinationSource: string;
  coordinatedSpdReference: string;
  manufacturerReference: string;
  leadRoute: LeadRouteChoice;
  leadVoltageKv: string;
  leadVoltageSource: string;
  leadLengthM: string;
  leadLengthSource: string;
  leadDropKvPerM: string;
  leadDropSource: string;
  ucMinimumV: string;
  ucMinimumSource: string;
  utRequiredV: string;
  utRequiredSource: string;
  iimpRequiredKa: string;
  iimpRequiredSource: string;
  inRequiredKa: string;
  inRequiredSource: string;
  imaxRequiredKa: string;
  imaxRequiredSource: string;
  items: ProtectedItemDraft[];
  upstreamSpdCode: string;
  downstreamSpdCode: string;
  jurisdictionProfile: string;
  notes: string;
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

const protectedItemDraftSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    uwKv: z.string(),
    uwSource: z.string(),
    distanceM: z.string(),
    notes: z.string(),
  })
  .strict();

// The shape a draft kept in the browser tab must still have to be restored: exactly the fields
// above. A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const surgeProtectionDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    spdCode: z.string(),
    locationKind: chosenOrBlank(locationKindSchema),
    systemVoltageV: z.string(),
    systemVoltageSource: z.string(),
    earthingSystem: chosenOrBlank(earthingSystemSchema),
    connectionMode: chosenOrBlank(connectionModeSchema),
    connectionModeSource: z.string(),
    sldBusCode: z.string(),
    currentSource: z.enum(CURRENT_SOURCES),
    faultRunId: z.string(),
    prospectiveCurrentKa: z.string(),
    prospectiveCurrentSource: z.string(),
    lpsPresence: chosenOrBlank(lpsPresenceSchema),
    spdType: chosenOrBlank(spdTypeSchema),
    upKv: z.string(),
    upSource: z.string(),
    upBasis: chosenOrBlank(upBasisSchema),
    ucV: z.string(),
    ucSource: z.string(),
    utV: z.string(),
    utSource: z.string(),
    iimpKa: z.string(),
    iimpSource: z.string(),
    inKa: z.string(),
    inSource: z.string(),
    imaxKa: z.string(),
    imaxSource: z.string(),
    iimpPerModeDeclared: z.boolean(),
    isccrKa: z.string(),
    isccrSource: z.string(),
    backupKind: chosenOrBlank(backupDeviceKindSchema),
    backupRatingA: z.string(),
    backupSource: z.string(),
    backupSldDeviceCode: z.string(),
    protectionDistanceM: z.string(),
    protectionDistanceSource: z.string(),
    coordination: chosenOrBlank(coordinationDeclarationSchema),
    coordinationSource: z.string(),
    coordinatedSpdReference: z.string(),
    manufacturerReference: z.string(),
    leadRoute: z.enum(LEAD_ROUTES),
    leadVoltageKv: z.string(),
    leadVoltageSource: z.string(),
    leadLengthM: z.string(),
    leadLengthSource: z.string(),
    leadDropKvPerM: z.string(),
    leadDropSource: z.string(),
    ucMinimumV: z.string(),
    ucMinimumSource: z.string(),
    utRequiredV: z.string(),
    utRequiredSource: z.string(),
    iimpRequiredKa: z.string(),
    iimpRequiredSource: z.string(),
    inRequiredKa: z.string(),
    inRequiredSource: z.string(),
    imaxRequiredKa: z.string(),
    imaxRequiredSource: z.string(),
    items: z.array(protectedItemDraftSchema).min(1),
    upstreamSpdCode: z.string(),
    downstreamSpdCode: z.string(),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict();

let nextItemNumber = 1;

// A prefix of its own, so a protected item id is never mistaken for another form's.
export function createProtectedItemId(): string {
  const id = `spd-item-${nextItemNumber}`;
  nextItemNumber += 1;
  return id;
}

// Item ids come from a counter that starts again at 1 on every page load, while a restored draft
// brings the ids it was saved with. Moving the counter past every id in use keeps an item added
// afterwards from taking the id of one already on screen.
export function seedProtectedItemIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^spd-item-(\d+)$/.exec(id);
    if (match !== null) {
      nextItemNumber = Math.max(nextItemNumber, Number(match[1]) + 1);
    }
  }
}

export function createProtectedItemDraft(): ProtectedItemDraft {
  return {
    id: createProtectedItemId(),
    code: "",
    name: "",
    uwKv: "",
    uwSource: "",
    distanceM: "",
    notes: "",
  };
}

// Only the defaults the backend itself has are filled in: the IN profile, the LPS unknown, the
// current at which Up is stated not stated, the coordination not declared. Every other choice
// starts blank; one empty protected item waits for the engineer; the prospective current on a
// linked fault run; the leads as a length with its drop.
export function createInitialSurgeProtectionDraft(): SurgeProtectionDraft {
  return {
    code: "",
    name: "",
    spdCode: "",
    locationKind: "",
    systemVoltageV: "",
    systemVoltageSource: "",
    earthingSystem: "",
    connectionMode: "",
    connectionModeSource: "",
    sldBusCode: "",
    currentSource: "LINKED",
    faultRunId: "",
    prospectiveCurrentKa: "",
    prospectiveCurrentSource: "",
    lpsPresence: "UNKNOWN",
    spdType: "",
    upKv: "",
    upSource: "",
    upBasis: "NOT_STATED",
    ucV: "",
    ucSource: "",
    utV: "",
    utSource: "",
    iimpKa: "",
    iimpSource: "",
    inKa: "",
    inSource: "",
    imaxKa: "",
    imaxSource: "",
    iimpPerModeDeclared: false,
    isccrKa: "",
    isccrSource: "",
    backupKind: "",
    backupRatingA: "",
    backupSource: "",
    backupSldDeviceCode: "",
    protectionDistanceM: "",
    protectionDistanceSource: "",
    coordination: "NOT_DECLARED",
    coordinationSource: "",
    coordinatedSpdReference: "",
    manufacturerReference: "",
    leadRoute: "LENGTH",
    leadVoltageKv: "",
    leadVoltageSource: "",
    leadLengthM: "",
    leadLengthSource: "",
    leadDropKvPerM: "",
    leadDropSource: "",
    ucMinimumV: "",
    ucMinimumSource: "",
    utRequiredV: "",
    utRequiredSource: "",
    iimpRequiredKa: "",
    iimpRequiredSource: "",
    inRequiredKa: "",
    inRequiredSource: "",
    imaxRequiredKa: "",
    imaxRequiredSource: "",
    items: [createProtectedItemDraft()],
    upstreamSpdCode: "",
    downstreamSpdCode: "",
    jurisdictionProfile: "IN",
    notes: "",
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// A declared figure with its source, required: always sent (a blank value as "" so the schema
// names it by its label), the source only when typed.
function required(key: string, figure: string, source: string): Record<string, unknown> {
  return { [key]: { value: figure.trim(), ...optional("source", source) } };
}

// A declared figure with its source, optional: sent when its figure is typed, left out when blank.
function declared(key: string, figure: string, source: string): Record<string, unknown> {
  const value = figure.trim();
  return value === "" ? {} : { [key]: { value, ...optional("source", source) } };
}

// The prospective current as chosen on screen: the linked run's id alone (a blank id is sent as
// "" so the schema names it), the declared figure alone, or nothing (NOT ESTABLISHED).
function currentPayload(draft: SurgeProtectionDraft): Record<string, unknown> {
  if (draft.currentSource === "LINKED") {
    return { fault_run_id: draft.faultRunId.trim() };
  }
  if (draft.currentSource === "DECLARED") {
    return required("prospective_current", draft.prospectiveCurrentKa, draft.prospectiveCurrentSource);
  }
  return {};
}

// The leads as chosen on screen: the voltage alone, the length with its drop, or nothing.
function leadsPayload(draft: SurgeProtectionDraft): Record<string, unknown> {
  if (draft.leadRoute === "VOLTAGE") {
    return { leads: required("lead_voltage_kv", draft.leadVoltageKv, draft.leadVoltageSource) };
  }
  if (draft.leadRoute === "LENGTH") {
    return {
      leads: {
        ...required("lead_length_m", draft.leadLengthM, draft.leadLengthSource),
        ...required("lead_inductive_drop_kv_per_m", draft.leadDropKvPerM, draft.leadDropSource),
      },
    };
  }
  return {};
}

// The back-up device: sent when its rating is typed (the kind left out when unchosen, so the
// schema names it), left out when blank.
function backupDevicePayload(draft: SurgeProtectionDraft): Record<string, unknown> {
  const rating = draft.backupRatingA.trim();
  if (rating === "" && draft.backupKind === "") {
    return {};
  }
  return {
    backup_device: {
      ...optional("kind", draft.backupKind),
      rating_a: rating,
      ...optional("source", draft.backupSource),
      ...optional("sld_device_code", draft.backupSldDeviceCode),
    },
  };
}

function spdPayload(draft: SurgeProtectionDraft): Record<string, unknown> {
  return {
    ...optional("spd_type", draft.spdType),
    ...required("up_kv", draft.upKv, draft.upSource),
    ...optional("up_basis", draft.upBasis),
    ...required("uc_v", draft.ucV, draft.ucSource),
    ...declared("ut_v", draft.utV, draft.utSource),
    ...declared("iimp_ka", draft.iimpKa, draft.iimpSource),
    ...declared("in_ka", draft.inKa, draft.inSource),
    ...declared("imax_ka", draft.imaxKa, draft.imaxSource),
    ...(draft.iimpPerModeDeclared ? { iimp_per_mode_declared: true } : {}),
    ...declared("isccr_ka", draft.isccrKa, draft.isccrSource),
    ...backupDevicePayload(draft),
    ...declared("protection_distance_m", draft.protectionDistanceM, draft.protectionDistanceSource),
    ...optional("coordination", draft.coordination),
    ...optional("coordination_source", draft.coordinationSource),
    ...optional("coordinated_spd_reference", draft.coordinatedSpdReference),
    ...optional("manufacturer_reference", draft.manufacturerReference),
  };
}

function requirementsPayload(draft: SurgeProtectionDraft): Record<string, unknown> {
  const requirements = {
    ...declared("uc_minimum_v", draft.ucMinimumV, draft.ucMinimumSource),
    ...declared("ut_required_v", draft.utRequiredV, draft.utRequiredSource),
    ...declared("iimp_required_ka", draft.iimpRequiredKa, draft.iimpRequiredSource),
    ...declared("in_required_ka", draft.inRequiredKa, draft.inRequiredSource),
    ...declared("imax_required_ka", draft.imaxRequiredKa, draft.imaxRequiredSource),
  };
  return Object.keys(requirements).length === 0 ? {} : { requirements };
}

// One protected item as the request carries it.
function itemPayload(item: ProtectedItemDraft): Record<string, unknown> {
  return {
    code: item.code.trim(),
    name: item.name.trim(),
    ...required("uw_kv", item.uwKv, item.uwSource),
    ...optional("distance_m", item.distanceM),
    ...optional("notes", item.notes),
  };
}

// The request body as the form will send it. It is validated afterwards with
// surgeProtectionRunCreateRequestSchema, so this function does not judge the values and never
// reorders the items.
export function buildSurgeProtectionRunPayload(
  draft: SurgeProtectionDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      spd_code: draft.spdCode.trim(),
      ...optional("location_kind", draft.locationKind),
      ...required("system_voltage_v", draft.systemVoltageV, draft.systemVoltageSource),
      ...optional("earthing_system", draft.earthingSystem),
      ...optional("connection_mode", draft.connectionMode),
      ...optional("connection_mode_source", draft.connectionModeSource),
      ...optional("sld_bus_code", draft.sldBusCode),
      ...currentPayload(draft),
      ...optional("lps_presence", draft.lpsPresence),
      spd: spdPayload(draft),
      ...leadsPayload(draft),
      ...requirementsPayload(draft),
      // Every item is sent, so the number in a message such as "Item 2 — Item code is required."
      // always matches the item's position on screen.
      items: draft.items.map(itemPayload),
      ...optional("upstream_spd_code", draft.upstreamSpdCode),
      ...optional("downstream_spd_code", draft.downstreamSpdCode),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

export const CURRENT_SOURCE_LABELS: Record<CurrentSourceChoice, string> = {
  LINKED: "Stored fault run of this revision (Ik'')",
  DECLARED: "Declared current with its source",
  NONE: "Not established (the rating is not checked)",
};

export const LEAD_ROUTE_LABELS: Record<LeadRouteChoice, string> = {
  LENGTH: "Lead length × declared inductive drop",
  VOLTAGE: "Lead voltage drop declared directly",
  NONE: "Not declared (the protection level is not established)",
};

export {
  BACKUP_DEVICE_KIND_LABELS,
  CONNECTION_MODE_LABELS,
  COORDINATION_LABELS,
  EARTHING_SYSTEM_LABELS,
  LOCATION_LABELS,
  LPS_LABELS,
  SPD_TYPE_LABELS,
  SURGE_PROTECTION_LABELS,
  UP_BASIS_LABELS,
};
